import React, { useState } from 'react'
import {
  XMarkIcon,
  CreditCardIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import { hubnaiAPI } from '@/services/api'

interface CreateHubnaiChargeCardModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface ChargeResult {
  id: number
  hubnai_charge_id: string
  amount: number
  payment_method: string
  status: string
}

const CreateHubnaiChargeCardModal: React.FC<CreateHubnaiChargeCardModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false)
  const [chargeResult, setChargeResult] = useState<ChargeResult | null>(null)

  // Form state
  const [formData, setFormData] = useState({
    amount: '', // Em reais
    description: '',
    payment_method: 'credit_card' as 'credit_card' | 'debit_card',
    customer_name: '',
    customer_email: '',
    customer_document: '',
    // Card data
    card_number: '',
    card_holder_name: '',
    card_expiration_month: '',
    card_expiration_year: '',
    card_cvv: ''
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      setLoading(true)

      // Converter valor de reais para centavos
      const amountInCents = Math.round(parseFloat(formData.amount) * 100)

      // Preparar dados do pagamento conforme novo backend (estrutura Hubnai)
      const paymentData: any = {
        amount: amountInCents,
        order_id: `ORDER-${Date.now()}`, // Gerar order_id único
        payment: {
          type: formData.payment_method === 'credit_card' ? 'credit' : 'debit',
          installments: 1,
          capture: true, // Captura automática
          has_interest: false,
          card: {
            holder: formData.card_holder_name,
            number: formData.card_number.replace(/\s/g, ''),
            expiryMonth: formData.card_expiration_month.padStart(2, '0'),
            expiryYear: formData.card_expiration_year,
            cvv: formData.card_cvv
          }
        },
        customer: {
          name: formData.customer_name || 'Cliente',
          document: formData.customer_document ? formData.customer_document.replace(/\D/g, '') : '00000000000',
          email: formData.customer_email,
        }
      }

      console.log('📤 Criando pagamento Cartão:', {
        ...paymentData,
        payment: { ...paymentData.payment, card: { ...paymentData.payment.card, number: '****', cvv: '***' } }
      })

      const result = await hubnaiAPI.createCardPayment(paymentData)

      console.log('✅ Pagamento Cartão criado:', result)

      // Adaptar resultado para o formato esperado pelo componente
      setChargeResult({
        id: 0, // Não temos ID local ainda
        hubnai_charge_id: result.id,
        amount: result.amount,
        payment_method: formData.payment_method,
        status: result.status === 3 ? 'paid' : result.status === 7 ? 'failed' : 'pending'
      })

      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      console.error('❌ Erro ao criar pagamento Cartão:', error)
      alert('Erro ao criar pagamento Cartão: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100)
  }

  const formatCardNumber = (value: string) => {
    // Formatar como: 1234 5678 9012 3456
    return value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim()
  }

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value)
    setFormData(prev => ({ ...prev, card_number: formatted }))
  }

  const handleClose = () => {
    setChargeResult(null)
    setFormData({
      amount: '',
      description: '',
      payment_method: 'credit_card',
      customer_name: '',
      customer_email: '',
      customer_document: '',
      card_number: '',
      card_holder_name: '',
      card_expiration_month: '',
      card_expiration_year: '',
      card_cvv: ''
    })
    onClose()
  }

  if (!isOpen) return null

  // Se já temos resultado, mostra confirmação
  if (chargeResult) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircleIcon className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Cobrança Processada!</h2>
                  <p className="text-sm text-gray-500">ID: {chargeResult.hubnai_charge_id}</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Info */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Valor:</span>
                <span className="font-semibold text-gray-900">{formatCurrency(chargeResult.amount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Método:</span>
                <span className="font-semibold text-gray-900 uppercase">
                  {chargeResult.payment_method === 'credit_card' ? 'Cartão de Crédito' : 'Cartão de Débito'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Status:</span>
                <span className="font-semibold text-green-700 uppercase">{chargeResult.status}</span>
              </div>
            </div>

            {/* Status Details */}
            {chargeResult.status === 'paid' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  ✅ <strong>Pagamento aprovado!</strong> A transação foi processada com sucesso e será sincronizada automaticamente.
                </p>
              </div>
            )}

            {chargeResult.status === 'pending' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  ⏳ <strong>Processando pagamento...</strong> A transação está sendo analisada pela operadora.
                </p>
              </div>
            )}

            {chargeResult.status === 'failed' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">
                  ❌ <strong>Pagamento recusado.</strong> Verifique os dados do cartão e tente novamente.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Concluir
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Form para criar cobrança
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <CreditCardIcon className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Nova Cobrança Cartão</h2>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Dados da Cobrança */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Dados da Cobrança</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor (R$) *
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  step="0.01"
                  min="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="29.90"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Cartão *
                </label>
                <select
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="credit_card">Cartão de Crédito</option>
                  <option value="debit_card">Cartão de Débito</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição *
                </label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: Pagamento de serviço"
                />
              </div>
            </div>
          </div>

          {/* Dados do Cartão */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Dados do Cartão</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número do Cartão *
                </label>
                <input
                  type="text"
                  name="card_number"
                  value={formData.card_number}
                  onChange={handleCardNumberChange}
                  required
                  maxLength={19} // 16 digits + 3 spaces
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                  placeholder="1234 5678 9012 3456"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome no Cartão *
                </label>
                <input
                  type="text"
                  name="card_holder_name"
                  value={formData.card_holder_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                  placeholder="JOÃO DA SILVA"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mês *
                  </label>
                  <input
                    type="text"
                    name="card_expiration_month"
                    value={formData.card_expiration_month}
                    onChange={handleChange}
                    required
                    maxLength={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="12"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ano *
                  </label>
                  <input
                    type="text"
                    name="card_expiration_year"
                    value={formData.card_expiration_year}
                    onChange={handleChange}
                    required
                    maxLength={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="2025"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CVV *
                  </label>
                  <input
                    type="text"
                    name="card_cvv"
                    value={formData.card_cvv}
                    onChange={handleChange}
                    required
                    maxLength={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="123"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Dados do Cliente (Opcional) */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Dados do Cliente</h3>
            <p className="text-sm text-gray-500 mb-4">Opcional - Preencha para vincular a um cliente</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Completo
                </label>
                <input
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="João da Silva"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="customer_email"
                  value={formData.customer_email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="joao@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CPF/CNPJ
                </label>
                <input
                  type="text"
                  name="customer_document"
                  value={formData.customer_document}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="000.000.000-00"
                />
              </div>
            </div>
          </div>

          {/* Security Note */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-xs text-gray-600">
              🔒 Seus dados de cartão são criptografados e processados com segurança. Nós nunca armazenamos informações completas do cartão.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Processando...
                </>
              ) : (
                <>
                  <CreditCardIcon className="w-5 h-5" />
                  Processar Pagamento
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateHubnaiChargeCardModal
